import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getCabinetRouteForUser } from '@/lib/roleRoutes';
import { routes } from '@/lib/routes';
import { portalApi } from '../api';
import type { PublicPageOut } from '../types';

const HELP = [
  {
    id: 'quickstart',
    aliases: ['start', 'quickStart'],
    title: 'Быстрый старт',
    text: 'Как войти, выбрать роль, открыть кабинет и пользоваться общей публичной средой.',
    cta: 'Открыть инструкцию',
    action: 'login' as const,
  },
  {
    id: 'role',
    aliases: ['roles'],
    title: 'Инструкция моей роли',
    text: 'Короткая пользовательская справка с deep links в соответствующие функции вашей роли.',
    cta: 'Открыть для моей роли',
    action: 'cabinet' as const,
  },
  {
    id: 'faq',
    aliases: [],
    title: 'Частые вопросы',
    text: 'Публикации, модерация, сохранения, источники, файлы и статусы.',
    cta: 'Смотреть FAQ',
    action: 'faq' as const,
  },
  {
    id: 'support',
    aliases: [],
    title: 'Поддержка',
    text: 'Если инструкция не помогла, обратитесь в поддержку.',
    cta: 'Открыть поддержку',
    action: 'support' as const,
  },
];

const FAQ = [
  [
    'Почему моя публикация не появилась сразу?',
    'Публикации обычных ролей переходят в статус «На модерации». После решения Owner/модератора объект становится публичным.',
  ],
  [
    'Где искать исследовательский опрос?',
    'Научный/исследовательский опрос создаётся в «Программы и инструменты», а здесь Owner может создать только быстрый общественный опрос.',
  ],
  [
    'Откуда система берёт конкурсы?',
    'Автосбор запускается ежедневно в 04:00 Asia/Almaty только по реестру разрешённых официальных источников.',
  ],
  [
    'Что означает «Официальный источник»?',
    'Это первичная страница организации. Для внешних возможностей перед действием всегда перепроверяйте актуальные условия там.',
  ],
] as const;

export const InstructionSection = ({ focusId }: { focusId?: string }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [page, setPage] = useState<PublicPageOut | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi
      .getPage('instruction')
      .then((data) => setPage({ slug: 'instruction', content_blocks: data.content_blocks || [] }))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, []);

  const resolvedFocus = (() => {
    if (!focusId) return '';
    if (focusId === 'start' || focusId === 'quickStart') return 'quickstart';
    return focusId;
  })();

  useEffect(() => {
    if (!resolvedFocus || loading) return;
    document.getElementById(`portal-${resolvedFocus}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [resolvedFocus, loading, page]);

  if (loading) {
    return <div className="public-empty">Загрузка...</div>;
  }

  const blocks = page?.content_blocks || [];
  const blocksMap = new Map(blocks.map((b) => [b.block_type, b.content]));

  const textFor = (keys: string[], fallback: string) => {
    for (const key of keys) {
      const value = blocksMap.get(key)?.text;
      if (typeof value === 'string' && value.trim()) return value;
    }
    return fallback;
  };

  const onHelp = (action: (typeof HELP)[number]['action']) => {
    if (action === 'faq') {
      document.getElementById('portal-faq-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (action === 'login' && !user) {
      navigate(routes.login);
      return;
    }
    if (action === 'cabinet') {
      navigate(user ? getCabinetRouteForUser(user) : routes.login);
      return;
    }
    if (action === 'support') {
      navigate(user ? routes.newRequest : routes.login);
    }
  };

  return (
    <div className="portal-section">
      <div className="public-hero">
        <div>
          <span className="public-eyebrow">Помощь</span>
          <h2>Инструкция</h2>
          <p>Быстрый старт, роль, FAQ и поддержка — в одном разделе.</p>
        </div>
      </div>

      <section className="portal-help-grid">
        {HELP.map((item, index) => (
          <article key={item.id} id={`portal-${item.id}`}>
            <span>0{index + 1}</span>
            <h2>{item.title}</h2>
            <p>{textFor([item.id, ...item.aliases], item.text)}</p>
            <button type="button" onClick={() => onHelp(item.action)}>
              {item.cta} →
            </button>
          </article>
        ))}
      </section>

      <section className="portal-faq" id="portal-faq-list">
        <h2>Частые вопросы</h2>
        {FAQ.map(([q, a]) => (
          <details key={q}>
            <summary>{q}</summary>
            <p>{a}</p>
          </details>
        ))}
      </section>
    </div>
  );
};
